import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import {
  Award, CheckCircle2, XCircle, AlertTriangle, Download,
  ShieldCheck, RefreshCw, FileCheck, Save, RotateCcw, UserCog,
} from 'lucide-react';
import { useCohortCertification, type ParticipantCertStatus } from '@/hooks/useCohortCertification';
import { generateDiplomaPDF, buildPublicCertUrl } from '@/lib/generateDiplomaPDF';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock } from 'lucide-react';

interface Props {
  cohortId: string;
  programId: string;
  organizationId: string | null;
}

export function CohortCertificationTab({ cohortId, programId, organizationId }: Props) {
  const cert = useCohortCertification(cohortId, programId);
  const { currentRole } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  const [pendingCertId, setPendingCertId] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmOne, setConfirmOne] = useState<ParticipantCertStatus | null>(null);
  const [confirmReissue, setConfirmReissue] = useState<ParticipantCertStatus | null>(null);
  const [confirmReissueAll, setConfirmReissueAll] = useState(false);
  const [manualFor, setManualFor] = useState<ParticipantCertStatus | null>(null);
  const [manualDiplomaType, setManualDiplomaType] = useState<'A' | 'B' | 'NONE'>('A');
  const [manualReason, setManualReason] = useState('');
  const [manualConfirmOpen, setManualConfirmOpen] = useState(false);

  const handleSaveCert = async () => {
    if (!pendingCertId) return;
    try {
      await cert.setCohortCertificate.mutateAsync(pendingCertId);
      setPendingCertId(null);
      toast({ title: 'Certificado asignado', description: 'La cohorte usará este certificado para emitir diplomas.' });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const eligible = cert.validationData.filter(p => p.assignedDiploma !== null && !p.certificateId);
  const alreadyGenerated = cert.validationData.filter(p => !!p.certificateId);

  const doGenerate = async (userIds: string[], reissue: boolean) => {
    try {
      const count = await cert.generateCertificates.mutateAsync({
        userIds,
        cohortId,
        programId,
        organizationId,
        participants: cert.validationData,
        reissue,
      });
      toast({
        title: reissue ? 'Diploma reemitido' : (count === 1 ? 'Diploma generado' : 'Diplomas generados'),
        description: reissue
          ? 'El diploma anterior fue reemplazado. El nuevo ID público ya está activo.'
          : `Se ${count === 1 ? 'generó' : 'generaron'} ${count} diploma(s).`,
      });
      cert.runValidation();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const openManualDialog = (p: ParticipantCertStatus) => {
    setManualFor(p);
    setManualDiplomaType(p.certificateDiplomaType ?? p.assignedDiploma ?? 'A');
    setManualReason('');
  };

  const handleManualIssue = async () => {
    if (!manualFor) return;
    try {
      await cert.issueManualCertificate.mutateAsync({
        userId: manualFor.user_id,
        cohortId,
        diplomaType: manualDiplomaType,
        reason: manualReason.trim() || undefined,
      });
      toast(
        manualDiplomaType === 'NONE'
          ? { title: 'Certificado retirado', description: `Se quitó el certificado de ${manualFor.full_name}.` }
          : { title: 'Certificado asignado manualmente', description: `Diploma ${manualDiplomaType} asignado a ${manualFor.full_name}.` }
      );
      setManualFor(null);
      cert.runValidation();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDownload = async (p: ParticipantCertStatus) => {
    if (!p.certificateId || !cert.selectedCertificate) return;
    const type = p.certificateDiplomaType ?? 'A';
    const c: any = cert.selectedCertificate;
    const prefix = type === 'A' ? 'diploma_a' : 'diploma_b';
    try {
      await generateDiplomaPDF({
        participantName: p.full_name,
        diplomaName: c[`${prefix}_name`] ?? 'Diploma',
        certificateId: p.certificateId,
        issuedAt: p.certificateDate ?? new Date().toISOString(),
        backgroundImageUrl: c[`${prefix}_image_url`] ?? null,
        publicUrl: buildPublicCertUrl(p.certificateId),
        entities: c[`${prefix}_entities`] ?? null,
        layout: c[`${prefix}_layout`] ?? null,
      });
      toast({ title: 'PDF descargado' });
    } catch (err: any) {
      toast({ title: 'Error al generar PDF', description: err.message, variant: 'destructive' });
    }
  };

  const currentCertId = cert.selectedCertificateId ?? '';
  const dropdownValue = pendingCertId ?? currentCertId ?? 'none';

  return (
    <div className="space-y-6">
      {!isSuperAdmin && (
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertDescription>
            Solo el Super Administrador puede emitir o reemitir diplomas. Tu acceso a este módulo es de solo lectura.
          </AlertDescription>
        </Alert>
      )}
      {/* Certificate selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Certificado de la cohorte
          </CardTitle>
          <CardDescription>
            Selecciona qué plantilla de certificado se usará para emitir diplomas a los participantes de esta cohorte.
            Cada plantilla define un Diploma A y un Diploma B con sus propios requisitos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cert.programCertificates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No hay plantillas de certificado configuradas para esta ruta. Crea una desde Gestión de Certificados.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Plantilla de certificado</Label>
                <Select
                  value={dropdownValue || 'none'}
                  onValueChange={v => setPendingCertId(v === 'none' ? null : v)}
                  disabled={!isSuperAdmin}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un certificado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguno</SelectItem>
                    {cert.programCertificates.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.private_name}
                        {c.diploma_a_name && ` — A: ${c.diploma_a_name}`}
                        {c.diploma_b_name && ` / B: ${c.diploma_b_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isSuperAdmin && pendingCertId !== null && pendingCertId !== currentCertId && (
                <Button onClick={handleSaveCert} disabled={cert.setCohortCertificate.isPending}>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar selección
                </Button>
              )}
              {cert.selectedCertificate && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="rounded-lg border p-4 bg-card/50">
                    <p className="text-xs text-muted-foreground mb-1">Diploma A (prioridad {cert.selectedCertificate.diploma_a_priority})</p>
                    <p className="font-medium">{cert.selectedCertificate.diploma_a_name ?? '—'}</p>
                  </div>
                  <div className="rounded-lg border p-4 bg-card/50">
                    <p className="text-xs text-muted-foreground mb-1">Diploma B (prioridad {cert.selectedCertificate.diploma_b_priority})</p>
                    <p className="font-medium">{cert.selectedCertificate.diploma_b_name ?? '—'}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Validation */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-primary" />
                Validación de Participantes
              </CardTitle>
              <CardDescription>El sistema asigna automáticamente el diploma de mayor prioridad que cumpla cada participante</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => cert.runValidation()}
                disabled={cert.validationLoading || !cert.selectedCertificate}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${cert.validationLoading ? 'animate-spin' : ''}`} />
                Validar
              </Button>
              {isSuperAdmin && cert.selectedCertificate && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmReissueAll(true)}
                    disabled={
                      cert.generateCertificates.isPending ||
                      cert.validationLoading ||
                      alreadyGenerated.length === 0
                    }
                    title={
                      alreadyGenerated.length === 0
                        ? 'No hay diplomas ya emitidos para reemitir'
                        : 'Reemite todos los diplomas ya emitidos (los IDs cambiarán)'
                    }
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reemitir todos{alreadyGenerated.length > 0 ? ` (${alreadyGenerated.length})` : ''}
                  </Button>
                  <Button
                    onClick={() => setConfirmAll(true)}
                    disabled={
                      cert.generateCertificates.isPending ||
                      cert.validationLoading ||
                      eligible.length === 0
                    }
                    title={
                      cert.validationData.length === 0
                        ? 'Primero presiona "Validar" para identificar participantes elegibles'
                        : eligible.length === 0
                          ? 'No hay participantes nuevos elegibles para generar'
                          : undefined
                    }
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Generar{eligible.length > 0 ? ` (${eligible.length})` : ''}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!cert.selectedCertificate ? (
            <p className="text-center text-muted-foreground py-8">
              Selecciona primero una plantilla de certificado para esta cohorte
            </p>
          ) : cert.validationData.length === 0 && !cert.validationLoading ? (
            <p className="text-center text-muted-foreground py-8">
              Presiona "Validar" para verificar el estado de los participantes
            </p>
          ) : cert.validationLoading ? (
            <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card className="bg-card/50 border-border/50">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{eligible.length}</p>
                    <p className="text-xs text-muted-foreground">Elegibles</p>
                  </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/50">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-destructive">
                      {cert.validationData.filter(p => p.assignedDiploma === null && !p.certificateId).length}
                    </p>
                    <p className="text-xs text-muted-foreground">No cumplen</p>
                  </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/50">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-primary">{alreadyGenerated.length}</p>
                    <p className="text-xs text-muted-foreground">Ya generados</p>
                  </CardContent>
                </Card>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Participante</TableHead>
                    <TableHead>Contenido</TableHead>
                    <TableHead>Entregables</TableHead>
                    <TableHead>Proyecto</TableHead>
                    <TableHead>Aprobado</TableHead>
                    <TableHead>Preguntas</TableHead>
                    <TableHead>Lab</TableHead>
                    <TableHead>Diploma a emitir</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cert.validationData.map(p => (
                    <TableRow key={p.user_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{p.full_name}</p>
                          <p className="text-xs text-muted-foreground">{p.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress value={p.contentPercent} className="h-2 flex-1" />
                          <span className="text-xs w-10 text-right">{p.contentPercent}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress value={p.deliverablesPercent} className="h-2 flex-1" />
                          <span className="text-xs w-10 text-right">{p.deliverablesPercent}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {p.projectSubmitted ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell>
                        {p.projectApproved ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell>
                        {p.projectAnswered ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell>
                        {p.labCompleted ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell>
                        {p.assignedDiploma ? (
                          <Badge className="bg-primary/20 text-primary border-primary/30">
                            <Award className="w-3 h-3 mr-1" />
                            Diploma {p.assignedDiploma}
                          </Badge>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="secondary" className="cursor-help">—</Badge>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs">
                                <p className="font-medium mb-1">No cumple ningún diploma:</p>
                                <ul className="text-xs space-y-0.5">
                                  {p.missingReasons.map((r, i) => (
                                    <li key={i} className="flex items-start gap-1">
                                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                      <span>{r}</span>
                                    </li>
                                  ))}
                                </ul>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          {p.certificateId ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Emitido {p.certificateDiplomaType ?? ''}
                            </Badge>
                          ) : p.assignedDiploma ? (
                            <Badge variant="outline">Listo</Badge>
                          ) : (
                            <Badge variant="secondary">Pendiente</Badge>
                          )}
                          {p.certificateIssuedManually && (
                            <Badge variant="outline" className="border-amber-500/40 text-amber-500">
                              Manual
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {p.certificateId ? (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleDownload(p)} title="Descargar">
                                <Download className="w-4 h-4" />
                              </Button>
                              {isSuperAdmin && p.assignedDiploma && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setConfirmReissue(p)}
                                  disabled={cert.generateCertificates.isPending}
                                  title="Reemitir"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          ) : isSuperAdmin && p.assignedDiploma ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmOne(p)}
                              disabled={cert.generateCertificates.isPending}
                            >
                              <Award className="w-4 h-4 mr-1" />Generar
                            </Button>
                          ) : null}
                          {isSuperAdmin && cert.selectedCertificate && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openManualDialog(p)}
                              disabled={cert.issueManualCertificate.isPending}
                              title="Asignar manualmente"
                            >
                              <UserCog className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirm: generar todos */}
      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar emisión masiva</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a emitir {eligible.length} diploma(s). Los participantes podrán verlos y descargarlos
              inmediatamente desde su perfil. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmAll(false);
                doGenerate(eligible.map(p => p.user_id), false);
              }}
            >
              Sí, emitir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm: generar uno */}
      <AlertDialog open={!!confirmOne} onOpenChange={(o) => !o && setConfirmOne(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar emisión</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a emitir el diploma para <strong>{confirmOne?.full_name}</strong>. El participante lo verá
              inmediatamente en su perfil. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmOne) doGenerate([confirmOne.user_id], false);
                setConfirmOne(null);
              }}
            >
              Sí, emitir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm: reemitir todos */}
      <AlertDialog open={confirmReissueAll} onOpenChange={setConfirmReissueAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar reemisión masiva</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a reemitir <strong>{alreadyGenerated.length}</strong> diploma(s) ya emitidos.
              <br />
              <br />
              <strong className="text-destructive">Los IDs públicos cambiarán</strong> y los
              anteriores dejarán de ser accesibles. Cualquier link compartido previamente
              (LinkedIn, WhatsApp, etc.) apuntará a URLs muertas. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmReissueAll(false);
                doGenerate(alreadyGenerated.map(p => p.user_id), true);
              }}
            >
              Sí, reemitir todos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm: reemitir */}
      <AlertDialog open={!!confirmReissue} onOpenChange={(o) => !o && setConfirmReissue(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar reemisión</AlertDialogTitle>
            <AlertDialogDescription>
              Esto reemplazará el diploma actual de <strong>{confirmReissue?.full_name}</strong>. El ID público
              cambiará y el anterior dejará de ser accesible. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmReissue) doGenerate([confirmReissue.user_id], true);
                setConfirmReissue(null);
              }}
            >
              Sí, reemitir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Asignación manual */}
      <Dialog open={!!manualFor} onOpenChange={(o) => !o && setManualFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{manualDiplomaType === 'NONE' ? 'Quitar certificado' : 'Asignar certificado manualmente'}</DialogTitle>
            <DialogDescription>
              {manualDiplomaType === 'NONE' ? (
                <>
                  Vas a quitarle el certificado actual a <strong>{manualFor?.full_name}</strong>. Esta acción
                  queda registrada en la auditoría con el motivo que indiques.
                </>
              ) : (
                <>
                  Vas a asignarle un diploma a <strong>{manualFor?.full_name}</strong> sin depender de la
                  validación automática. Esto queda registrado como emisión manual y con motivo en la auditoría.
                  {manualFor?.certificateId && ' Reemplazará el certificado que ya tiene.'}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Tipo de diploma</Label>
              <Select value={manualDiplomaType} onValueChange={(v) => setManualDiplomaType(v as 'A' | 'B' | 'NONE')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">
                    Diploma A{cert.selectedCertificate?.diploma_a_name ? ` — ${cert.selectedCertificate.diploma_a_name}` : ''}
                  </SelectItem>
                  <SelectItem value="B">
                    Diploma B{cert.selectedCertificate?.diploma_b_name ? ` — ${cert.selectedCertificate.diploma_b_name}` : ''}
                  </SelectItem>
                  <SelectItem value="NONE">Sin certificado (quita el actual)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Textarea
                value={manualReason}
                onChange={(e) => setManualReason(e.target.value)}
                placeholder={manualDiplomaType === 'NONE' ? 'Ej: se asignó por error, revocado a solicitud del participante…' : 'Ej: excepción autorizada, corrección de datos, caso especial…'}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualFor(null)}>Cancelar</Button>
            <Button
              onClick={() => setManualConfirmOpen(true)}
              disabled={cert.issueManualCertificate.isPending}
              variant={manualDiplomaType === 'NONE' ? 'destructive' : 'default'}
            >
              {cert.issueManualCertificate.isPending
                ? 'Guardando…'
                : manualDiplomaType === 'NONE' ? 'Sí, quitar certificado' : 'Confirmar asignación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Segunda confirmación (advertencia) antes de ejecutar el cambio manual */}
      <AlertDialog open={manualConfirmOpen} onOpenChange={setManualConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {manualDiplomaType === 'NONE' ? '¿Borrar este certificado?' : '¿Cambiar este certificado?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {manualDiplomaType === 'NONE' ? (
                <>
                  Confirma que quieres borrar el certificado de <strong>{manualFor?.full_name}</strong>.
                  Esta acción no se puede deshacer.
                </>
              ) : (
                <>
                  Confirma que quieres {manualFor?.certificateId ? 'cambiar' : 'asignar'} el certificado de{' '}
                  <strong>{manualFor?.full_name}</strong> a <strong>Diploma {manualDiplomaType}</strong>.
                  {manualFor?.certificateId && ' El certificado actual será reemplazado.'}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setManualConfirmOpen(false);
                handleManualIssue();
              }}
            >
              {manualDiplomaType === 'NONE' ? 'Sí, borrar' : 'Sí, confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
